// ── Forest Tab Bar: Horizontal Canvas Navigation ─────────────────
// Shows canvas tabs with status, back button, and action buttons.

import React from 'react';
import { useForest } from './ForestContext';

const STATUS_ICONS = {
  pending: '○',
  generating: '◌',
  ready: '●',
  error: '✗',
};

const STATUS_COLORS = {
  pending: '#555',
  generating: '#6c63ff',
  ready: '#20c997',
  error: '#ff4757',
};

// Render snake_case canvas keys as readable labels.
// Decompose LLM returns "target_audience_acquisition" — show "Target audience acquisition".
function humanize(s) {
  if (!s) return '';
  if (/[A-Z]/.test(s) && /\s/.test(s)) return s;
  return s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, c => c.toUpperCase());
}

export default function ForestTabBar({ onExit }) {
  const ctx = useForest();
  if (!ctx?.plan) return null;

  const {
    plan,
    activeCanvasKey,
    setActiveCanvas,
    forestCanvases,
    isGenerating,
    generateAll,
    runCritique,
    stopGeneration,
    crossRefs,
    crossBlends,
  } = ctx;

  const allReady = forestCanvases.every(c => c.status === 'ready');
  const anyGenerating = forestCanvases.some(c => c.status === 'generating');
  const anyPending = forestCanvases.some(c => c.status === 'pending');
  const readyCount = forestCanvases.filter(c => c.status === 'ready').length;
  const generatingCount = forestCanvases.filter(c => c.status === 'generating').length;
  const generatingNow = forestCanvases.find(c => c.status === 'generating');

  const progressLabel = isGenerating
    ? generatingNow
      ? `⟳ Generating: ${humanize(generatingNow.title)} (${readyCount}/${forestCanvases.length} done)`
      : '⟳ Decomposing...'
    : null;
  const progressPct = forestCanvases.length
    ? Math.round((readyCount / forestCanvases.length) * 100)
    : 0;

  return (
    <div className="forest-tab-bar">
      {/* Header row: back button + title + actions */}
      <div className="forest-tab-bar-header">
        {onExit && (
          <button className="forest-back-btn" onClick={onExit} title="Back to sessions">
            ← Back
          </button>
        )}
        <div className="forest-tab-bar-title">
          <span className="forest-tab-bar-icon">◈</span>
          <span className="forest-tab-bar-idea">{plan.idea?.slice(0, 60) || 'Forest'}</span>
          <span className="forest-tab-bar-stats">{readyCount}/{forestCanvases.length} canvases</span>
        </div>
        {progressLabel && (
          <div
            className="forest-tab-bar-progress"
            role="status"
            aria-live="polite"
            aria-label={progressLabel}
          >
            <span className="forest-tab-bar-progress-label">{progressLabel}</span>
            <div className="forest-tab-bar-progress-track">
              <div
                className="forest-tab-bar-progress-bar"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}
        <div className="forest-tab-bar-actions">
          {anyPending && !anyGenerating && (
            <button className="forest-action-btn forest-action-generate" onClick={generateAll}>
              ▶ Generate All
            </button>
          )}
          {(anyGenerating || isGenerating) && (
            <button className="forest-action-btn forest-action-stop" onClick={stopGeneration}>
              ■ Stop
            </button>
          )}
          {allReady && !isGenerating && (
            <button className="forest-action-btn forest-action-critique" onClick={runCritique}>
              ⚔ Critique
            </button>
          )}
        </div>
      </div>

      {/* Canvas tabs row */}
      <div className="forest-tab-bar-tabs">
        {/* Overview tab */}
        <div
          className={`forest-tab ${activeCanvasKey === '__meta__' ? 'active' : ''}`}
          onClick={() => setActiveCanvas('__meta__')}
        >
          <span className="forest-tab-status" style={{ color: '#6c63ff' }}>◉</span>
          <span className="forest-tab-label">Overview</span>
          {crossRefs?.length > 0 && (
            <span className="forest-tab-badge">{crossRefs.length}</span>
          )}
        </div>

        {/* Canvas tabs */}
        {forestCanvases.map((canvas) => {
          const status = canvas.status || 'pending';
          const isActive = activeCanvasKey === canvas.canvasKey;
          return (
            <div
              key={canvas.canvasKey}
              className={`forest-tab ${isActive ? 'active' : ''} ${status === 'generating' ? 'generating' : ''}`}
              onClick={() => setActiveCanvas(canvas.canvasKey)}
              title={canvas.description}
            >
              <span className="forest-tab-status" style={{ color: STATUS_COLORS[status] }}>
                {STATUS_ICONS[status]}
              </span>
              <span className="forest-tab-label">{humanize(canvas.title) || canvas.canvasKey}</span>
              {canvas.nodes?.length > 0 && (
                <span className="forest-tab-count">{canvas.nodes.length}</span>
              )}
            </div>
          );
        })}

        {/* Synthesis tab — cross-canvas blending. See features/ai-workflows/forest-cross-canvas-blending.feature */}
        <div
          className={`forest-tab ${activeCanvasKey === '__synthesis__' ? 'active' : ''}`}
          onClick={() => setActiveCanvas('__synthesis__')}
          title="Cross-canvas synthesis: splice high-novelty ideas from different canvases into emergent bets"
        >
          <span className="forest-tab-status" style={{ color: 'var(--info, #a78bfa)' }}>🌉</span>
          <span className="forest-tab-label">Synthesis</span>
          {crossBlends?.length > 0 && (
            <span className="forest-tab-count">{crossBlends.length}</span>
          )}
        </div>
      </div>
    </div>
  );
}
